import errorResolver from "./errorResolver";
import { resolveNoContent, resolveWithBody } from "./Utils/utils";
import { usePromisedRequest as promiseRequest } from "../Common/request";
import { PaymentStatus } from "../Common/consts";
import { GenderType, Status } from "../Common/Competitors/types";
import { DeleteRegistrationPreviewStatus, EventRegistrationSideEffect } from "../Common/types";

export type EventRegistrationUpdateByOrganizer = {
    parameterId: number;
    academyId: number;
    branch: string;
};

// Mirrors CompetitorCategory.php (all fields are required by the backend)
export type CompetitorCategory = {
    competitorId: number;
    parameterId: number;
    academyId: number;
    branch: string;
};

// Mirrors CompetitorRegistrationRequest.php
export type CompetitorRegistrationRequest = {
    eventId: number;
    userId: number;
    competitorCategories: CompetitorCategory[];
};

export type EventRegisteredCompetitorWithCategory = {
    eventCompetitorId: number;
    competitorId: number;
    firstName: string;
    lastName: string;
    category: string;
    academy: string;
    branch: string;
    parameterId: number;
    created: string;
    paymentComplete: boolean;
    paymentAmount: number;
    paymentStatus: PaymentStatus;
    paymentFormatted: string;
    discountPercentage?: number;
    changeAllowedAfterDueDate: boolean;
    photoUrl: string | null;
};

export type FullEventMyCompetitorView = {
    id: number;
    parameterId: number;
    academyId: number;
    branch: string;
    gender: GenderType;
    birthYear: number;
    birthDate: string | null;
};

export type OrganizerEventRegistrationUpdateResponse = {
    sideEffects: Array<EventRegistrationSideEffect>;
};

export type DeleteRegistrationPreviewResponse = {
    status: DeleteRegistrationPreviewStatus;
    potentialSideEffects: Array<EventRegistrationSideEffect>;
};

export type EventRegistrationUpdate = {
    eventCompetitorId: number;
    parameterId: number;
    academyId: number;
    branch: string;
};

export type CompetitorWithCategory = {
    id: number;
    firstName: string;
    lastName: string;
    academy: string;
    branch: string | undefined | null;
    category: string;
    status: Status;
    eventRegistrationSpotNumber: number | null;
};

export default {
    //Used for verification.
    getAllCompetitors(eventId: number): Promise<CompetitorWithCategory[]> {
        return promiseRequest<CompetitorWithCategory[]>({
            url: `/api/admin/events/${eventId}/competitors`,
            method: "get",
            json: true,
        }).then(resolveWithBody());
    },
    getEventCompetitorForUser(eventId: number, userId: number, eventCompetitorId: number): Promise<FullEventMyCompetitorView> {
        return promiseRequest<FullEventMyCompetitorView>({
            url: `/api/events/${eventId}/users/${userId}/competitors/${eventCompetitorId}`,
            json: true,
            method: "get",
        }).then(resolveWithBody());
    },
    getAllEventCompetitorsForUser(eventId: number, userId: number): Promise<EventRegisteredCompetitorWithCategory[]> {
        return promiseRequest<EventRegisteredCompetitorWithCategory[]>({
            url: `/api/events/${eventId}/users/${userId}/competitors`,
            method: "get",
            json: true,
        }).then(res => {
            if (res.statusCode !== 200) {
                throw Error("Could not fetch competitors registered to event.");
            }

            return res.body;
        });
    },

    submitRegistrations(eventId: number, userId: number, payload: CompetitorRegistrationRequest): Promise<void> {
        return promiseRequest({
            url: `/api/events/${eventId}/users/${userId}/competitors`,
            method: "post",
            json: payload,
        }).then(res => {
            errorResolver.throwValidationErrors(res);

            if (res.statusCode === 409) {
                throw Error("flash_messages.cannot_register_competitor_due_conflict");
            }
            if (res.statusCode !== 204) {
                throw Error("Could not register competitor to event.");
            }
        });
    },
    removeCompetitorRegistration(eventId: number, userId: number, eventCompetitorId: number): Promise<void> {
        return promiseRequest<void>({
            url: `/api/events/${eventId}/users/${userId}/competitors/${eventCompetitorId}`,
            method: "delete",
            json: true,
        }).then(resolveNoContent());
    },
    copyToCategory(
        eventId: number,
        eventRegistrationId: number,
        targetParameterId: number,
    ): Promise<OrganizerEventRegistrationUpdateResponse> {
        return promiseRequest<OrganizerEventRegistrationUpdateResponse>({
            url: `/api/admin/events/${eventId}/competitors/${eventRegistrationId}`,
            json: {
                eventCompetitorId: Number(eventRegistrationId),
                targetParameterId: Number(targetParameterId),
            },
            method: "post",
        }).then(resolveWithBody());
    },
    updateRegistration(
        eventId: number,
        eventRegistrationId: number,
        input: EventRegistrationUpdateByOrganizer,
    ): Promise<OrganizerEventRegistrationUpdateResponse> {
        return promiseRequest<OrganizerEventRegistrationUpdateResponse>({
            url: `/api/admin/events/${eventId}/competitors/${eventRegistrationId}`,
            json: {
                eventRegistrationId: eventRegistrationId,
                parameterId: input.parameterId,
                academyId: input.academyId,
                branch: input.branch,
            },
            method: "put",
        }).then(resolveWithBody());
    },
    updateEventRegistrationByUser(eventId: number, userId: number, request: EventRegistrationUpdate) {
        return promiseRequest({
            url: `/api/events/${eventId}/users/${userId}/competitors/${request.eventCompetitorId}`,
            method: "put",
            json: request,
        }).then(resolveNoContent());
    },
    deleteRegistration(eventId: number, eventRegistrationId: number): Promise<OrganizerEventRegistrationUpdateResponse> {
        return promiseRequest<OrganizerEventRegistrationUpdateResponse>({
            url: `/api/admin/events/${eventId}/competitors/${eventRegistrationId}`,
            method: "delete",
            json: true,
        }).then(resolveWithBody());
    },
    getDeletePreview(eventId: number, eventRegistrationId: number): Promise<DeleteRegistrationPreviewResponse> {
        return promiseRequest<DeleteRegistrationPreviewResponse>({
            url: `/api/admin/events/${eventId}/competitors/${eventRegistrationId}/delete-preview`,
            method: "get",
            json: true,
        }).then(resolveWithBody());
    },
    restoreRegistration(eventId: number, eventRegistrationId: number): Promise<void> {
        return promiseRequest<void>({
            url: `/api/admin/events/${eventId}/competitors/${eventRegistrationId}/restore`,
            method: "post",
            json: true,
        }).then(resolveNoContent());
    },
};
